import type { StaffResponse } from '../../api/generated';

const COMPETENCE_SEPARATOR = ', ';

// Named once rather than restated inline at every hand-off. Readonly because it is a memoized
// lookup shared across the subtree: a consumer mutating it would change what others read with
// no re-render.
export type CompetenceNameById = ReadonlyMap<string, string>;

/**
 * Resolves stored preferred-staff ids to roster entries, for display only.
 *
 * Ordered by the patient's stored ids rather than by roster order, so the read-only view and the
 * edit form list the same people in the same places and a newly added one appears where it was
 * added. Ids the roster does not cover are skipped, and repeats collapse, so a caller can render
 * straight from this without duplicate React keys. Never use the result to build what gets saved:
 * a miss here must cost a row, not the stored id.
 */
export const resolvePreferredStaff = (
  preferredStaffIds: readonly string[],
  allStaff: readonly StaffResponse[]
): StaffResponse[] => {
  const staffById = new Map(allStaff.map((member) => [member.id, member]));
  const seen = new Set<string>();
  const resolved: StaffResponse[] = [];

  for (const id of preferredStaffIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    const member = staffById.get(id);
    if (member !== undefined) resolved.push(member);
  }

  return resolved;
};

export const resolveCompetenceNames = (
  staff: StaffResponse,
  competenceNameById: CompetenceNameById
): string[] =>
  staff.staffProfessionIds
    .map((id) => competenceNameById.get(id))
    .filter((name): name is string => name !== undefined);

/**
 * Option label for the staff picker's dropdown list. Competences are appended as text
 * rather than as Tags (which the read-only view uses) to keep two staff with similar
 * names distinguishable on a single option line.
 */
export const formatStaffOptionLabel = (
  staff: StaffResponse,
  competenceNameById: CompetenceNameById
): string => {
  const competences = resolveCompetenceNames(staff, competenceNameById);
  if (competences.length === 0) return staff.fullName;
  return `${staff.fullName} (${competences.join(COMPETENCE_SEPARATOR)})`;
};
